"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment } from "@opensea/graphql";
import { Item, ItemContent, ItemTitle, Link } from "@opensea/next-ui-kit";
import { FlexCenter, NumberDisplay, TABLE_SIZES, TableCell, TableRow, TextBody, TextLabel, TextOverflowTooltip, tableRowSizeVariants, useTable } from "@opensea/ui-kit";
import { useContextSelector } from "use-context-selector";
import { CollectionSettingsContext } from "@/components/collection/CollectionSettingsProvider";
import { getCollectionUrlByTrait } from "@/components/collection/utils";
import { ItemAvatar, ItemAvatarFragment } from "@/components/items/avatar/ItemAvatar";
import { ItemPreviewTooltip } from "@/components/items/ItemPreviewTooltip";
import { ItemPreviewTooltipFragment } from "@/components/items/ItemPreviewTooltip/ItemPreviewTooltip.fragment";
import { itemUrl, itemUrlFragment } from "@/components/items/utils";
import { TokenPrice } from "@/components/price/TokenPrice";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { RarityChip } from "@/components/rarity/RarityChip";
import { CollectionTraitItemsTableSkeletonRow } from "./CollectionTraitItemsTableSkeletonRow";
import { COLLECTION_TRAIT_ITEMS_TABLE_COLUMN_CLASSNAMES } from "./styles";
export const CollectionTraitItemsTableRowFragment = graphql(`
    fragment CollectionTraitItemsTableRow on CollectionAttributeFlatValue {
      count
      traitType
      traitValue
      percent
      floorPrice {
        pricePerItem {
          ...TokenPrice
        }
      }
      topOffer {
        pricePerItem {
          ...TokenPrice
        }
      }
      previewItems(limit: 5) {
        id
        ...ItemAvatar
        ...ItemPreviewTooltip
        ...itemUrl
      }
    }
  `, [ItemAvatarFragment, TokenPriceFragment, ItemPreviewTooltipFragment, itemUrlFragment]);
export function CollectionTraitItemsTableRow(t0) {
  const $ = _c(17);
  const {
    item: itemKey,
    context
  } = t0;
  const item = readFragment(CollectionTraitItemsTableRowFragment, itemKey);
  const showRarity = useContextSelector(CollectionSettingsContext, _temp);
  const {
    size
  } = useTable();
  if (!item) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CollectionTraitItemsTableSkeletonRow />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const {
    image
  } = tableRowSizeVariants({
    size
  });
  const imageSize = TABLE_SIZES[size].image;
  const extraItemsCount = item.count - (item.previewItems?.length ?? 0);
  const t1 = showRarity ? <RarityChip count={item.count} percentage={item.percent} /> : <TextBody>{item.count}</TextBody>;
  let t2;
  if ($[1] !== t1) {
    t2 = <TableCell className={COLLECTION_TRAIT_ITEMS_TABLE_COLUMN_CLASSNAMES.traitCount}>{t1}</TableCell>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const T0 = TableCell;
  const t3 = COLLECTION_TRAIT_ITEMS_TABLE_COLUMN_CLASSNAMES;
  const T1 = FlexCenter;
  const t4 = "gap-2";
  const t5 = item.previewItems?.map(previewItem => <ItemPreviewTooltip item={previewItem} key={previewItem.id}><ItemAvatar className={image()} item={previewItem} size={imageSize} /></ItemPreviewTooltip>);
  let t6;
  if ($[3] !== extraItemsCount) {
    t6 = extraItemsCount > 0 && <TextLabel className="ml-0.5" size="xs"><NumberDisplay display="quantity" prefix="+" value={extraItemsCount} /></TextLabel>;
    $[3] = extraItemsCount;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== T1 || $[6] !== t5 || $[7] !== t6) {
    t7 = <T1 className={t4}>{t5}{t6}</T1>;
    $[5] = T1;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== T0 || $[10] !== t3.items || $[11] !== t7) {
    t8 = <T0 className={t3.items}>{t7}</T0>;
    $[9] = T0;
    $[10] = t3.items;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = item.floorPrice?.pricePerItem;
  let t10;
  if ($[13] !== t9) {
    t10 = <TableCell className={COLLECTION_TRAIT_ITEMS_TABLE_COLUMN_CLASSNAMES.traitFloor}><TextBody><TokenPrice price={t9} /></TextBody></TableCell>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = item.topOffer?.pricePerItem;
  let t12;
  if ($[15] !== t11) {
    t12 = <TableCell className={COLLECTION_TRAIT_ITEMS_TABLE_COLUMN_CLASSNAMES.bestOffer}><TextBody><TokenPrice price={t11} /></TextBody></TableCell>;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return <Link href={item.previewItems?.length === 1 ? itemUrl(item.previewItems[0]) : getCollectionUrlByTrait(context?.collectionSlug ?? "", {
    traitType: item.traitType,
    values: [item.traitValue]
  })} variant="unstyled"><TableRow interactive={true}><TableCell className={COLLECTION_TRAIT_ITEMS_TABLE_COLUMN_CLASSNAMES.trait} stickyLeft={0}><Item variant="unstyled"><ItemContent><TextOverflowTooltip><ItemTitle weight="regular">{item.traitValue}</ItemTitle></TextOverflowTooltip><TextOverflowTooltip><TextLabel color="text-secondary">{item.traitType}</TextLabel></TextOverflowTooltip></ItemContent></Item></TableCell>{t2}{t8}{t10}{t12}</TableRow></Link>;
}
function _temp(state) {
  return state.showRarity;
}